/**
 * Reading a publish's progress off the `tenancy_solution_deployments` rows.
 *
 * These rows are the only server-side record of HOW a publish went that every viewer can
 * see: a solution version has no `FAILED` status (it returns to `ACTIVE` on failure exactly
 * as on success), so before this the frontend inferred failure from a module-level Set that
 * only the publisher's own browser ever wrote to — a coworker was always told the publish
 * succeeded.
 *
 * One row is created per publish attempt, so `id` is a stable key for announcing an outcome
 * exactly once, across tabs and across users.
 */

/** Terminal success — `DEPLOYMENT_STATUS.Finished` on the backend. */
const FINISHED = 'finished';

/** Terminal failure — either bare `failed` or a step-qualified `"<step> - Failed"`. */
const FAILED = 'failed';

export type DeploymentPhase = 'in_progress' | 'succeeded' | 'failed';

/**
 * A deployment row, flattened. Structural rather than the generated type: every Hasura
 * scalar here comes through codegen as `any`, and the helpers below are unit-tested.
 *
 * Note `deployedBy` is the user's **id** — the Hasura table stores the uuid, unlike the
 * remote-schema `SolutionDeployment.deployedBy`, which is an object (see `DeploymentLike`
 * in derive-deployments.ts). Resolve it against `ctx.projectUsers` for a name.
 */
export type DeploymentProgressRow = {
  id: string;
  environment: string;
  status: string;
  deployedBy?: string | null;
  deployedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  isRepublish?: boolean | null;
  isLatest?: boolean | null;
  solutionVersionId?: string | null;
  versionName?: string | null;
  runs?: {
    id: string;
    status?: string | null;
    updatedAt?: string | null;
  }[];
};

/** The subscription payload's shape, before flattening. Scalars arrive as `any`. */
type RawDeploymentRow = {
  id?: unknown;
  environment?: unknown;
  status?: unknown;
  deployedBy?: unknown;
  deployedAt?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
  isRepublish?: unknown;
  isLatest?: unknown;
  solutionVersionId?: unknown;
  solution_version?: { name?: unknown } | null;
  solution_deployment_runs?:
    | ({
        id?: unknown;
        status?: unknown;
        updatedAt?: unknown;
      } | null)[]
    | null;
};

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

/**
 * Flattens the subscription rows into `DeploymentProgressRow`, dropping anything without an
 * id, an environment or a status — those three are what every consumer keys on.
 */
export const toDeploymentProgressRows = (
  rows: (RawDeploymentRow | null | undefined)[] | null | undefined
): DeploymentProgressRow[] =>
  (rows ?? []).reduce<DeploymentProgressRow[]>((result, row) => {
    const id = asText(row?.id);
    const environment = asText(row?.environment);
    const status = asText(row?.status);
    if (!id || !environment || !status) return result;

    result.push({
      id,
      environment,
      status,
      deployedBy: asText(row?.deployedBy),
      deployedAt: asText(row?.deployedAt),
      createdAt: asText(row?.createdAt),
      updatedAt: asText(row?.updatedAt),
      isRepublish: Boolean(row?.isRepublish),
      isLatest: Boolean(row?.isLatest),
      solutionVersionId: asText(row?.solutionVersionId),
      versionName: asText(row?.solution_version?.name),
      runs: (row?.solution_deployment_runs ?? []).flatMap((run) => {
        const runId = asText(run?.id);
        return runId
          ? [
              {
                id: runId,
                status: asText(run?.status),
                updatedAt: asText(run?.updatedAt),
              },
            ]
          : [];
      }),
    });

    return result;
  }, []);

/**
 * Whether a publish is still running, and if not, how it ended.
 *
 * Anything that is not one of the two terminal forms is in flight — and in that case the
 * status string itself names the step the deploy is on, which is what `readDeploymentStep`
 * hands to the UI.
 */
export const readDeploymentPhase = (
  status?: string | null
): DeploymentPhase => {
  const value = (status ?? '').trim().toLowerCase();
  if (value === FINISHED) return 'succeeded';
  // `failed`, `Build - Failed`, `Deploy — failed`: the backend qualifies the failure with
  // the step that failed, so match the suffix rather than the whole string.
  if (value === FAILED || value.endsWith(FAILED)) return 'failed';
  return 'in_progress';
};

/**
 * The step a status names, for display: the whole string while running, and the part before
 * the `- Failed` suffix once it failed. `undefined` when the status carries no step (a bare
 * `failed`, or a finished deploy).
 */
export const readDeploymentStep = (
  status?: string | null
): string | undefined => {
  const value = (status ?? '').trim();
  if (!value) return undefined;

  const phase = readDeploymentPhase(value);
  if (phase === 'succeeded') return undefined;
  if (phase === 'in_progress') return value;

  // Strip the trailing separator + `Failed` only. Splitting on the first dash would cut a
  // step name that contains one ("Set-up environment - Failed" -> "Set").
  const step = value.replace(/\s*[-–—]\s*failed$/i, '').trim();
  return !step || step.toLowerCase() === FAILED ? undefined : step;
};

/**
 * When a row last changed. `updatedAt` is the row's own clock and moves with every step, so
 * it orders attempts correctly even before `deployedAt` is set.
 */
export const readDeploymentTime = (row: DeploymentProgressRow): number => {
  const stamp = row.updatedAt || row.deployedAt || row.createdAt;
  const time = stamp ? new Date(stamp).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
};

const newestFirst = (
  a: DeploymentProgressRow,
  b: DeploymentProgressRow
): number => readDeploymentTime(b) - readDeploymentTime(a);

/** Newest-first copy, so callers never depend on the order the subscription happens to send. */
export const sortDeploymentsNewestFirst = (
  rows: DeploymentProgressRow[] | null | undefined
): DeploymentProgressRow[] => [...(rows ?? [])].sort(newestFirst);

/**
 * The most recent deployment per environment — what the Versions environment cards show.
 * Includes running and failed attempts on purpose: the card's job is to report the latest
 * attempt, not only the last successful one (which `deriveDeployedVersions` already gives).
 */
export const pickLatestDeploymentPerEnvironment = (
  rows: DeploymentProgressRow[] | null | undefined
): Record<string, DeploymentProgressRow> => {
  const latest: Record<string, DeploymentProgressRow> = {};

  for (const row of sortDeploymentsNewestFirst(rows)) {
    const environment = (row.environment ?? '').toLowerCase();
    if (environment && !latest[environment]) latest[environment] = row;
  }

  return latest;
};

/** The newest deployment still running, if any — the one the in-progress toast describes. */
export const pickRunningDeployment = (
  rows: DeploymentProgressRow[] | null | undefined
): DeploymentProgressRow | undefined =>
  sortDeploymentsNewestFirst(rows).find(
    (row) => readDeploymentPhase(row.status) === 'in_progress'
  );

/** The newest deployment of one specific solution version, whatever its phase. */
export const pickDeploymentForVersion = (
  rows: DeploymentProgressRow[] | null | undefined,
  solutionVersionId?: string | null
): DeploymentProgressRow | undefined =>
  solutionVersionId
    ? sortDeploymentsNewestFirst(rows).find(
        (row) => row.solutionVersionId === solutionVersionId
      )
    : undefined;

/**
 * The deploy log as text, for the read-only editor the Versions page opens on a failed
 * publish.
 *
 * `structuredLogs` is preferred when present — it groups the output by step and names the one
 * that failed, which is the whole question being asked — with `rawLogs` as the fallback.
 * Returns undefined when there is nothing to show: the backend returns no logs at all when a
 * publish failed before its Gitea run started, which is exactly the case on QA1 today, so
 * the caller must say so rather than open an empty editor.
 */
export const buildDeploymentLogText = (
  logs?: {
    rawLogs?: unknown;
    structuredLogs?:
      | ({ name?: unknown; status?: unknown; lines?: unknown } | null)[]
      | null;
  } | null
): string | undefined => {
  const steps = (logs?.structuredLogs ?? []).flatMap((step) => {
    const name = asText(step?.name) ?? '';
    const status = asText(step?.status);
    const lines = Array.isArray(step?.lines)
      ? step.lines.map((line) => String(line))
      : [];
    if (!name && !lines.length) return [];
    return [[`── ${name}${status ? ` [${status}]` : ''}`, ...lines].join('\n')];
  });

  if (steps.length) return steps.join('\n\n');

  const raw = asText(logs?.rawLogs);
  return raw?.trim() ? raw : undefined;
};

/**
 * Who published, as a display name.
 *
 * The row stores only the user id, so the name has to come from the project's user list
 * (structural on purpose — `ProjectUser` lives in the XState types, which import this
 * module). `undefined` when the id is unknown there, e.g. a publish by someone since removed
 * from the project; callers must have wording that works without a name.
 */
export const readDeploymentAuthorName = (
  userId: string | null | undefined,
  users:
    | ({
        id: string;
        firstName?: string | null;
        lastName?: string | null;
      } | null)[]
    | null
    | undefined
): string | undefined => {
  if (!userId) return undefined;
  const user = (users ?? []).find((candidate) => candidate?.id === userId);
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name || undefined;
};

/**
 * Which publish attempt to describe, and how it is going. Pure — the caller keeps
 * `trackedDeploymentId` between calls and feeds back what this returns.
 *
 * Following one attempt by id, rather than re-picking a row every time, is what makes a
 * republish work: a republish deploys an OLD version, so its row does not belong to the draft
 * version the caller is subscribed to, and re-picking by version would lose it halfway.
 *
 * A row is only adopted while the version status says a publish is running. That is
 * deliberate: a row left non-terminal by a backend that died would otherwise read as "still
 * publishing" forever and hold every viewer in read-only mode. Tracking stops as soon as the
 * attempt reaches a terminal status.
 *
 * With no row to follow, the phase comes from the version status alone — the pre-deployment
 * -rows behaviour, which is also what a solution whose rows are unreadable falls back to. The
 * caller then has no attempt id, and so nothing to deduplicate an announcement on.
 */
export const trackPublishProgress = ({
  trackedDeploymentId,
  versionStatus,
  solutionVersionId,
  deployments,
}: {
  trackedDeploymentId?: string | null;
  versionStatus?: string | null;
  solutionVersionId?: string | null;
  deployments?: DeploymentProgressRow[] | null;
}): {
  phase: DeploymentPhase | 'idle';
  deployment?: DeploymentProgressRow;
  step?: string;
  trackedDeploymentId?: string;
} => {
  const rows = deployments ?? [];
  const isPublishing = versionStatus === 'PUBLISHING';

  const tracked =
    (trackedDeploymentId
      ? rows.find((row) => row.id === trackedDeploymentId)
      : undefined) ??
    (isPublishing
      ? (pickDeploymentForVersion(rows, solutionVersionId) ??
        pickRunningDeployment(rows))
      : undefined);

  if (tracked) {
    const phase = readDeploymentPhase(tracked.status);
    return {
      phase,
      deployment: tracked,
      step: readDeploymentStep(tracked.status),
      trackedDeploymentId: phase === 'in_progress' ? tracked.id : undefined,
    };
  }

  return { phase: isPublishing ? 'in_progress' : 'idle' };
};
